import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { SplitButton } from 'primereact/splitbutton';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Chip } from 'primereact/chip';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Timeline } from 'primereact/timeline';
import { Knob } from 'primereact/knob';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { Panel } from 'primereact/panel';
import { Badge } from 'primereact/badge';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Chart } from 'primereact/chart';
import { Image } from 'primereact/image';
import { Fieldset } from 'primereact/fieldset';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ProgressBar } from 'primereact/progressbar';
import { Avatar } from 'primereact/avatar';
import { Skeleton } from 'primereact/skeleton';
import { classNames } from 'primereact/utils';
import { getReportComponent, determineReportType } from '../config/reportTypeMapping';
import axiosInstance from '../config/axios.config';
import axios from 'axios';
// import './ReportViewer.css';

// Create a custom axios instance for API calls
const createApiClient = () => {
    const host = window.location.hostname;
    const protocol = window.location.protocol;
    const apiPort = window.location.port === '3000' ? ':8000' : window.location.port ? `:${window.location.port}` : '';
    const baseURL = `${protocol}//${host}${apiPort}/api`;
    
    console.log('ReportViewer API Base URL:', baseURL);
    
    const client = axios.create({
        baseURL,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        withCredentials: true,
    });
    
    // Add auth token
    client.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem('authToken');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );
    
    return client;
};

const ReportViewer = () => {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);
    
    // State management
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [reportDetails, setReportDetails] = useState(null);
    const [reportComponent, setReportComponent] = useState(null);
    const [error, setError] = useState(null);
    const [emailDialog, setEmailDialog] = useState(false);
    const [shareDialog, setShareDialog] = useState(false);
    const [compareMode, setCompareMode] = useState(false);
    const [compareReportId, setCompareReportId] = useState(null);
    const [emailData, setEmailData] = useState({
        to: '',
        subject: '',
        message: ''
    });
    
    // Load report data
    useEffect(() => {
        loadReportData();
    }, [reportId]);
    
    const loadReportData = async () => {
        if (!reportId) {
            setError('No report ID provided');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('Loading report data for ID:', reportId);
            const apiClient = createApiClient();
            const response = await apiClient.get(`/report-history/details/${reportId}`);
            
            if (response.data.success) {
                const reportDetails = response.data.data;
                console.log('Report Details:', reportDetails);
                
                setReportDetails(reportDetails);
                
                // Determine report type and get the appropriate component
                const reportType = determineReportType(reportDetails);
                console.log('Determined report type:', reportType);
                
                const componentConfig = getReportComponent(reportType);
                console.log('Component config:', componentConfig);
                
                if (componentConfig.component) {
                    // Get the component props using the mapping configuration
                    const componentProps = componentConfig.props(reportDetails, reportDetails);
                    console.log('Component props:', componentProps);
                    
                    setReportComponent({
                        Component: componentConfig.component,
                        props: componentProps,
                        reportType: reportType
                    });
                    
                    // Set the report data from the appropriate source
                    setReportData(componentProps.reportData || reportDetails);
                } else {
                    setError(`Unknown report type: ${reportType}`);
                }
            } else {
                setError(response.data.message || 'Failed to load report data');
            }
        } catch (error) {
            console.error('Error loading report:', error);
            setError(error.response?.data?.message || error.message || 'Failed to load report');
            
            // Show toast error
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load report data',
                    life: 3000
                });
            }
        } finally {
            setLoading(false);
        }
    };
    
    // generateSampleReport() lived here: 110 lines that invented a report type
    // with Math.random(), a customer name, credits used and processing time.
    // It was never called — dead scaffolding inside a live file, one call away
    // from showing a fabricated report to a DSA (GH #198).
    
    // Audit trail data
    const auditTrail = [
        { date: new Date(), user: 'John Doe', action: 'Generated Report', ip: '192.168.1.100' },
        { date: new Date(Date.now() - 3600000), user: 'Admin', action: 'Viewed Report', ip: '192.168.1.101' },
        { date: new Date(Date.now() - 7200000), user: 'Jane Smith', action: 'Downloaded PDF', ip: '192.168.1.102' }
    ];
    
    // Action handlers
    const handlePrint = () => {
        window.print();
        toast.current.show({
            severity: 'info',
            summary: 'Print',
            detail: 'Opening print preview...',
            life: 3000
        });
    };
    
    const downloadOptions = [
        {
            label: 'Download as PDF',
            icon: 'pi pi-file-pdf',
            command: () => {
                toast.current.show({
                    severity: 'success',
                    summary: 'Download Started',
                    detail: 'Downloading report as PDF',
                    life: 3000
                });
            }
        },
        {
            label: 'Download as Excel',
            icon: 'pi pi-file-excel',
            command: () => {
                toast.current.show({
                    severity: 'success',
                    summary: 'Download Started',
                    detail: 'Downloading report as Excel',
                    life: 3000
                });
            }
        },
        {
            label: 'Download as JSON',
            icon: 'pi pi-code',
            command: () => {
                const dataStr = JSON.stringify(reportData, null, 2);
                const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                const exportFileDefaultName = `report_${reportId}.json`;
                
                const linkElement = document.createElement('a');
                linkElement.setAttribute('href', dataUri);
                linkElement.setAttribute('download', exportFileDefaultName);
                linkElement.click();
                
                toast.current.show({
                    severity: 'success',
                    summary: 'Download Started',
                    detail: 'Downloading report as JSON',
                    life: 3000
                });
            }
        }
    ];
    
    const handleEmail = () => {
        if (!emailData.to || !emailData.subject) {
            toast.current.show({
                severity: 'error',
                summary: 'Missing Information',
                detail: 'Please fill in all required fields',
                life: 3000
            });
            return;
        }
        
        toast.current.show({
            severity: 'success',
            summary: 'Email Sent',
            detail: 'Report has been sent successfully',
            life: 3000
        });
        
        setEmailDialog(false);
        setEmailData({ to: '', subject: '', message: '' });
    };
    
    const handleShare = () => {
        const shareUrl = `${window.location.origin}/reports/${reportId}`;
        navigator.clipboard.writeText(shareUrl);
        toast.current.show({
            severity: 'success',
            summary: 'Link Copied',
            detail: 'Report link copied to clipboard',
            life: 3000
        });
        setShareDialog(false);
    };
    
    // Get score color based on value
    const getScoreColor = (score) => {
        if (score >= 750) return '#22c55e';
        if (score >= 650) return '#f59e0b';
        if (score >= 550) return '#fb923c';
        return '#ef4444';
    };
    
    // Toolbar templates
    const leftToolbarTemplate = () => {
        return (
            <div className="flex align-items-center gap-2">
                <Button
                    icon="pi pi-arrow-left"
                    rounded
                    text
                    severity="secondary"
                    onClick={() => navigate('/reports/history')}
                    tooltip="Back to Reports"
                    tooltipOptions={{ position: 'bottom' }}
                />
                <Divider layout="vertical" />
                <div>
                    <h2 className="m-0">{reportData?.title}</h2>
                    <p className="text-gray-600 text-sm m-0 mt-1">
                        Report ID: {reportData?.id} • Generated on {reportData?.generatedDate.toLocaleString('en-IN')}
                    </p>
                </div>
            </div>
        );
    };
    
    const rightToolbarTemplate = () => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-print"
                    label="Print"
                    severity="secondary"
                    onClick={handlePrint}
                />
                <SplitButton
                    label="Download"
                    icon="pi pi-download"
                    model={downloadOptions}
                    severity="primary"
                />
                <Button
                    icon="pi pi-envelope"
                    label="Email"
                    severity="secondary"
                    onClick={() => setEmailDialog(true)}
                />
                <Button
                    icon="pi pi-share-alt"
                    label="Share"
                    severity="secondary"
                    onClick={() => setShareDialog(true)}
                />
            </div>
        );
    };
    
    // Render content based on report type
    const renderPANReport = () => {
        const data = reportData.data;
        const verificationSteps = [
            { status: 'PAN Submitted', date: reportData.generatedDate, icon: 'pi pi-check', color: '#22c55e' },
            { status: 'Database Verified', date: reportData.generatedDate, icon: 'pi pi-check', color: '#22c55e' },
            { status: 'Details Matched', date: reportData.generatedDate, icon: 'pi pi-check', color: '#22c55e' },
            { status: 'Report Generated', date: reportData.generatedDate, icon: 'pi pi-check', color: '#22c55e' }
        ];
        
        return (
            <>
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <Card title="Verification Details">
                            <div className="verification-details">
                                <div className="detail-row">
                                    <span className="detail-label">PAN Number:</span>
                                    <span className="detail-value font-semibold">{data.panNumber}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Name:</span>
                                    <span className="detail-value">{data.name}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Name Match:</span>
                                    <Tag 
                                        value={data.nameMatch ? 'Matched' : 'Not Matched'} 
                                        severity={data.nameMatch ? 'success' : 'danger'}
                                    />
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Status:</span>
                                    <Tag value={data.status} severity="success" />
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Date of Birth:</span>
                                    <span className="detail-value">{data.dateOfBirth}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Aadhaar Linked:</span>
                                    <Tag 
                                        value={data.aadhaarLinked ? 'Yes' : 'No'} 
                                        severity={data.aadhaarLinked ? 'success' : 'warning'}
                                    />
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Category:</span>
                                    <span className="detail-value">{data.category}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Jurisdiction:</span>
                                    <span className="detail-value">{data.jurisdiction}</span>
                                </div>
                            </div>
                        </Card>
                    </div>
                    
                    <div className="col-12 md:col-6">
                        <Card title="Verification Timeline">
                            <Timeline 
                                value={verificationSteps} 
                                align="left"
                                className="verification-timeline"
                                marker={(item) => (
                                    <span 
                                        className="custom-marker shadow-2" 
                                        style={{ backgroundColor: item.color }}
                                    >
                                        <i className={item.icon}></i>
                                    </span>
                                )}
                                content={(item) => (
                                    <div>
                                        <p className="font-semibold m-0">{item.status}</p>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {item.date.toLocaleTimeString('en-IN')}
                                        </p>
                                    </div>
                                )}
                            />
                        </Card>
                    </div>
                </div>
            </>
        );
    };
    
    const renderEquifaxReport = () => {
        const data = reportData.data;
        
        const chartData = {
            labels: ['Payment History', 'Credit Utilization', 'Credit Age', 'New Credit'],
            datasets: [{
                label: 'Score Factors',
                data: [
                    data.scoreFactors.paymentHistory,
                    data.scoreFactors.creditUtilization,
                    data.scoreFactors.creditAge,
                    data.scoreFactors.newCredit * 10
                ],
                backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6']
            }]
        };
        
        return (
            <>
                <div className="grid">
                    <div className="col-12 md:col-4">
                        <Card title="Credit Score" className="text-center">
                            <Knob
                                value={data.creditScore}
                                min={data.scoreRange.min}
                                max={data.scoreRange.max}
                                size={200}
                                strokeWidth={8}
                                valueColor={getScoreColor(data.creditScore)}
                                rangeColor="#e5e7eb"
                                readOnly
                            />
                            <h3 className="mt-3">
                                {data.creditScore >= 750 ? 'Excellent' :
                                 data.creditScore >= 650 ? 'Good' :
                                 data.creditScore >= 550 ? 'Fair' : 'Poor'}
                            </h3>
                            <p className="text-gray-600">
                                Your score is better than 75% of users
                            </p>
                        </Card>
                    </div>
                    
                    <div className="col-12 md:col-8">
                        <Card title="Score Factors">
                            <Chart type="bar" data={chartData} options={{
                                indexAxis: 'y',
                                plugins: { legend: { display: false } },
                                scales: {
                                    x: { beginAtZero: true, max: 100 }
                                }
                            }} />
                        </Card>
                    </div>
                    
                    <div className="col-12">
                        <Card title="Account Summary">
                            <DataTable value={data.accounts} className="account-summary-table">
                                <Column field="type" header="Account Type" />
                                <Column field="bank" header="Bank" />
                                <Column field="status" header="Status" body={(rowData) => (
                                    <Tag 
                                        value={rowData.status} 
                                        severity={rowData.status === 'Active' ? 'success' : 'secondary'}
                                    />
                                )} />
                                <Column field="limit" header="Credit Limit" body={(rowData) => 
                                    `₹${rowData.limit.toLocaleString()}`
                                } />
                                <Column field="outstanding" header="Outstanding" body={(rowData) => 
                                    `₹${rowData.outstanding.toLocaleString()}`
                                } />
                            </DataTable>
                        </Card>
                    </div>
                </div>
            </>
        );
    };
    
    const renderBankReport = () => {
        const data = reportData.data;
        
        const trendChartData = {
            labels: data.monthlyTrend.map(m => m.month),
            datasets: [
                {
                    label: 'Credits',
                    data: data.monthlyTrend.map(m => m.credits),
                    borderColor: '#10B981',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    tension: 0.4
                },
                {
                    label: 'Debits',
                    data: data.monthlyTrend.map(m => m.debits),
                    borderColor: '#EF4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    tension: 0.4
                }
            ]
        };
        
        return (
            <>
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <Card className="metric-card">
                            <div className="metric-content">
                                <i className="pi pi-arrow-down text-green-500 text-2xl"></i>
                                <div>
                                    <p className="text-gray-600 m-0">Total Credits</p>
                                    <h3 className="m-0">₹{data.totalCredits.toLocaleString()}</h3>
                                </div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-3">
                        <Card className="metric-card">
                            <div className="metric-content">
                                <i className="pi pi-arrow-up text-red-500 text-2xl"></i>
                                <div>
                                    <p className="text-gray-600 m-0">Total Debits</p>
                                    <h3 className="m-0">₹{data.totalDebits.toLocaleString()}</h3>
                                </div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-3">
                        <Card className="metric-card">
                            <div className="metric-content">
                                <i className="pi pi-wallet text-blue-500 text-2xl"></i>
                                <div>
                                    <p className="text-gray-600 m-0">Avg Balance</p>
                                    <h3 className="m-0">₹{data.avgMonthlyBalance.toLocaleString()}</h3>
                                </div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-3">
                        <Card className="metric-card">
                            <div className="metric-content">
                                <i className="pi pi-sync text-purple-500 text-2xl"></i>
                                <div>
                                    <p className="text-gray-600 m-0">Transactions</p>
                                    <h3 className="m-0">{data.transactions}</h3>
                                </div>
                            </div>
                        </Card>
                    </div>
                    
                    <div className="col-12">
                        <Card title="Monthly Cash Flow Trend">
                            <Chart 
                                type="line" 
                                data={trendChartData} 
                                options={{
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { position: 'top' }
                                    },
                                    scales: {
                                        y: {
                                            ticks: {
                                                callback: function(value) {
                                                    return '₹' + value.toLocaleString();
                                                }
                                            }
                                        }
                                    }
                                }}
                                style={{ height: '300px' }}
                            />
                        </Card>
                    </div>
                    
                    <div className="col-12">
                        <Card title="Income Categories">
                            <DataTable value={data.categories}>
                                <Column field="category" header="Category" />
                                <Column field="amount" header="Amount" body={(rowData) => 
                                    `₹${rowData.amount.toLocaleString()}`
                                } />
                                <Column field="percentage" header="Percentage" body={(rowData) => (
                                    <div className="flex align-items-center gap-2">
                                        <ProgressBar 
                                            value={rowData.percentage} 
                                            showValue={false}
                                            style={{ height: '6px', width: '100px' }}
                                         aria-label="Progress" />
                                        <span>{rowData.percentage}%</span>
                                    </div>
                                )} />
                            </DataTable>
                        </Card>
                    </div>
                </div>
            </>
        );
    };
    
    const renderReportContent = () => {
        switch (reportData.type) {
            case 'PAN':
                return renderPANReport();
            case 'EQUIFAX':
                return renderEquifaxReport();
            case 'BANK':
                return renderBankReport();
            default:
                return <Message severity="info" text="Report type not supported" />;
        }
    };
    
    if (loading) {
        return (
            <div className="report-viewer-page p-4">
                <Card>
                    <Skeleton height="60px" className="mb-4" />
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <Skeleton height="300px" />
                        </div>
                        <div className="col-12 md:col-6">
                            <Skeleton height="300px" />
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="report-viewer-page p-4">
                <Toast ref={toast} />
                <Card>
                    <div className="text-center p-5">
                        <i className="pi pi-exclamation-triangle text-6xl text-red-500 mb-3"></i>
                        <h3 className="text-red-600 mb-2">Failed to Load Report</h3>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <Button 
                            label="Try Again" 
                            icon="pi pi-refresh" 
                            onClick={() => loadReportData()}
                            className="mr-2"
                        />
                        <Button 
                            label="Go Back" 
                            icon="pi pi-arrow-left" 
                            severity="secondary"
                            onClick={() => navigate('/report-history')}
                        />
                    </div>
                </Card>
            </div>
        );
    }

    if (!reportComponent) {
        return (
            <div className="report-viewer-page p-4">
                <Toast ref={toast} />
                <Card>
                    <div className="text-center p-5">
                        <i className="pi pi-info-circle text-6xl text-blue-500 mb-3"></i>
                        <h3 className="text-blue-600 mb-2">Report Component Not Available</h3>
                        <p className="text-gray-600 mb-4">The report component for this report type is not available.</p>
                        <Button 
                            label="Go Back" 
                            icon="pi pi-arrow-left" 
                            severity="secondary"
                            onClick={() => navigate('/report-history')}
                        />
                    </div>
                </Card>
            </div>
        );
    }
    
    return (
        <div className="report-viewer-page p-4">
            <Toast ref={toast} />
            
            <Card>
                {/* Header with basic info */}
                <div className="flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="m-0 text-primary">{reportComponent.reportType} Report</h2>
                        <p className="text-sm text-gray-600 m-0">
                            Report ID: {reportDetails?.id || reportId} • Generated: {reportDetails?.created_at ? new Date(reportDetails.created_at).toLocaleString('en-IN') : 'N/A'}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button 
                            label="Back to History" 
                            icon="pi pi-arrow-left" 
                            severity="secondary"
                            size="small"
                            onClick={() => navigate('/report-history')}
                        />
                    </div>
                </div>
                
                {/* Render the actual report component */}
                <div className="report-content">
                    {React.createElement(reportComponent.Component, reportComponent.props)}
                </div>
            </Card>
        </div>
    );
};

export default ReportViewer;